/**
 * The React surface of the translation runtime: two hooks and the switcher.
 *
 * There is no provider and no context. The locale is module state with a
 * subscription, so `useSyncExternalStore` is the whole binding, which also
 * means `t()` works identically inside a component and inside `lib/api.ts`,
 * where there is no React at all.
 */

import { useSyncExternalStore } from "react";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import type { Locale } from "../../shared/settings";
import { LOCALES } from "../../shared/settings";
import { applyDocumentLocale, rememberDeviceChoice } from "./detect";
import { switchLocale } from "./load";
import { getLocale, getVersion, subscribe } from "./runtime";
import { t } from "./t";

/**
 * `t`, plus a re-render when the locale changes. The function itself is stable;
 * the store's version is what invalidates the component.
 */
export function useT(): typeof t {
  useSyncExternalStore(subscribe, getVersion, getVersion);
  return t;
}

export function useLocale(): Locale {
  useSyncExternalStore(subscribe, getVersion, getVersion);
  return getLocale();
}

/** Switch, persist, and tell the document. Awaits the fetch of whatever the
 *  new locale still needs, so nothing renders half-translated. */
export async function chooseLocale(next: Locale): Promise<void> {
  rememberDeviceChoice(next);
  await switchLocale(next);
  applyDocumentLocale(next);
}

/**
 * Two chips. Short codes rather than flags: a flag is a country, and a language
 * is not, and «УКР» reads at a glance where a 🇺🇦 at 20px does not.
 *
 * Sized by the component like everything else (`src/ui/README.md` §4), so it is
 * 44px on a phone at a pitch and 36px on the organiser's laptop.
 *
 * `tone="band"` is not decoration. On `--band` the default variants invert:
 * selected is `--primary`, which IS the band's own ink and disappears into it,
 * and `outline` fills with `--card`, which is a white pill carrying white text.
 * The band tone marks selection with `--band-raised`, the same way the admin
 * sidebar marks its own.
 */
export function LocaleSwitcher({
  className,
  tone = "light",
}: {
  className?: string;
  tone?: "light" | "band";
}) {
  const locale = useLocale();
  const t = useT();
  return (
    <ButtonGroup aria-label={t("locale.label")} className={className}>
      {LOCALES.map((code) => {
        const on = code === locale;
        return (
          <Button
            key={code}
            size="sm"
            variant={tone === "band" ? "ghost" : on ? "default" : "outline"}
            aria-pressed={on}
            className={
              tone === "band"
                ? on
                  ? "bg-band-raised text-band-foreground hover:bg-band-raised"
                  : "text-band-muted hover:bg-band-raised/60 hover:text-band-foreground"
                : undefined
            }
            onClick={() => void chooseLocale(code)}
          >
            {code === "uk" ? t("locale.ukShort") : t("locale.enShort")}
          </Button>
        );
      })}
    </ButtonGroup>
  );
}

export type { MessageKey } from "./messages/schema";
export { t } from "./t";
